import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Subject } from 'rxjs';
import { take, takeUntil, finalize } from 'rxjs/operators';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { GradingType, GradingTypeLookup } from 'go-modules/activity-editor/activity-editor.controller';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { GoCdkPopoverDirective } from '@go-modules/directives/go-cdk-popover/go-cdk-popover.directive';
import { LibraryService } from '@go-modules/services/library/library.service';
import { PanelModule } from '@go-modules/components/panel/panel.module';
import { NgxToolbarModule } from '@go-modules/components/toolbar/toolbar.module';
import { NgxToolbarButtonModule } from '@go-modules/components/toolbar/toolbar-button/toolbar-button.module';
import { Action } from '@go-modules/components/toolbar/toolbar-button/action-to-icon-class-map';
import { rubricEditorModal, rubricEditorModalToken } from 'go-modules/modals/rubric-editor/modal.factory';
import { RubricTemplate } from '@go-modules/interfaces/rubrics/rubric';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { LibraryDialogComponent } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';

type GradingTypeKey = keyof GradingTypeLookup;

export const RUBRIC_VIEWER_PREVIEW_MODE = 'live';
export const RUBRIC_VIEWER_EDIT_MODE = 'edit';

@Component({
	selector: 'activity-grading',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [
		CommonModule,
		ReactiveFormsModule,
		TranslateModule,
		MatFormFieldModule,
		MatSelectModule,
		MatOptionModule,
		MatInputModule,
		MatTooltipModule,
		MatCheckboxModule,
		GoCdkPopoverDirective,
		PanelModule,
		NgxToolbarModule,
		NgxToolbarButtonModule,
		MatIconModule
	],
	templateUrl: './activity-grading.component.html',
	styleUrls: ['./activity-grading.component.scss']
})
export class ActivityGradingComponent implements OnInit, OnDestroy {
	@Input() public activity: ActivityModel;
	public gradingTypeLookup: GradingTypeLookup = {
		NOT_GRADED: {
			title: 'activity-editor-new_not-graded',
			description: 'activity-editor-new_not-graded-description'
		},
		POINTS: {
			title: 'activity-editor-new_points',
			description: 'activity-editor-new_points-description'
		},
		INSTRUCTOR_RUBRIC: {
			title: 'activity-editor-new_instructor-rubrics',
			description: 'activity-editor-new_instructor-rubrics-description'
		}
	};

	public gradingOptions: { key: GradingTypeKey; config: GradingType }[] = [];
	public gradingTypeControl: FormControl<GradingTypeKey>;
	public selectedRubric: RubricTemplate | null = null;
	public isLoadingRubric: boolean = false;
	public reviewersAccessControl: FormControl | null = null;
	public pointsControl: FormControl | null = null;
	public toolbarActions = Action;
	public selectedGradingTypeKey: GradingTypeKey = 'NOT_GRADED';
	public rubricTemplateControl: FormControl | null = null;
	private destroy$ = new Subject<void>();

	constructor (
		public activityFormService: ActivityFormService,
		private cdr: ChangeDetectorRef,
		private translate: TranslateService,
		private libraryService: LibraryService,
		private matDialog: MatDialog,
		private ngxGoModalService: GoModalService,
		@Inject(rubricEditorModalToken) private rubricEditor: ReturnType<typeof rubricEditorModal>
	) {
		this.gradingTypeControl = new FormControl<GradingTypeKey>('NOT_GRADED', { nonNullable: true });
		this.gradingTypeControl.addValidators(Validators.required);
	}

	public ngOnInit (): void {
		this.pointsControl = this.activityFormService.getControl('total_score');
		this.rubricTemplateControl = this.activityFormService.getControl('rubric_template_id');
		this.reviewersAccessControl = this.activityFormService.getControl('can_reviewers_see_grades');

		this.gradingTypeLookup.NOT_GRADED.disabled = this.isPointsReadonly;
		this.gradingOptions = [
			{ key: 'NOT_GRADED', config: this.gradingTypeLookup.NOT_GRADED },
			{ key: 'POINTS', config: this.gradingTypeLookup.POINTS },
			{ key: 'INSTRUCTOR_RUBRIC', config: this.gradingTypeLookup.INSTRUCTOR_RUBRIC }
		];

		const initialType = this.determineInitiallySelectedGradingType();
		this.selectedGradingTypeKey = initialType;
		this.gradingTypeControl.setValue(initialType, { emitEvent: false });
		this.updatePointsValidators();

		if (initialType === 'INSTRUCTOR_RUBRIC' && this.activity?.rubric_template_id) {
			this.loadSelectedRubric(this.activity.rubric_template_id);
		}

		// if rubric is in use then only show rubric grading type option
		// or else users can bypass rubric lockdown
		if (this.activity.rubric_template_id) {
			this.libraryService.checkRubricUsage(this.activity.rubric_template_id)
				.pipe(take(1))
				.subscribe((response) => {
					if (response.in_use && response.activities.includes(this.activity.activity_id)) {
						this.gradingOptions = [
							{ key: 'INSTRUCTOR_RUBRIC', config: this.gradingTypeLookup.INSTRUCTOR_RUBRIC }
						];
					}
				});
		}

		this.gradingTypeControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				if (value) {
					this.onGradingTypeSelectionChange(value);
				}
			});
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public shouldDisplayPointsPossible (): boolean {
		if (!this.pointsControl) {
			return false;
		}

		if (this.selectedGradingTypeKey === 'INSTRUCTOR_RUBRIC') {
			const value = this.pointsControl.value;
			return value !== null && value !== undefined;
		}

		return this.selectedGradingTypeKey === 'POINTS';
	}

	public isPointsPossibleDisabled (): boolean {
		return this.selectedGradingTypeKey === 'INSTRUCTOR_RUBRIC' || this.isPointsReadonly;
	}

	public getPointsDisabledTooltip (): string {
		if (this.selectedGradingTypeKey === 'INSTRUCTOR_RUBRIC') {
			return this.translate.instant('activity-editor-header_points-generated');
		}
		return this.translate.instant('activity-editor-header_points-lms');
	}

	public shouldDisplayPointsPossibleError (): boolean {
		return this.selectedGradingTypeKey === 'POINTS' &&
			this.pointsControl.invalid &&
			(this.pointsControl.touched || this.pointsControl.dirty);
	}

	public getSelectedGradingTitle (): string {
		const selected = this.gradingOptions.find((option) => option.key === this.selectedGradingTypeKey);
		return this.translate.instant(selected.config.title);
	}

	public normalizePointsPossible (): void {
		const value = this.pointsControl.value;
		if (value === null || value === undefined || value === '') {
			return;
		}

		const parsed = Number(value);
		if (isNaN(parsed)) {
			return;
		}

		const normalized = Math.round(parsed * 100) / 100;
		this.pointsControl.setValue(normalized, { emitEvent: false });
	}

	public handlePreview (): void {
		this.rubricEditor.open({
			modalData: {
				options: {
					mode: RUBRIC_VIEWER_PREVIEW_MODE,
					previewOnly: true,
					postData: {
						group_id: this.activity.group_id
					},
					schemaId: this.selectedRubric.id
				}
			}
		});
	}

	public handleEdit (): void {
		this.libraryService.checkRubricUsage(this.selectedRubric.id).subscribe((response) => {
			// if rubric is in use then no editing allowed
			if (response.in_use) {
				// in use on this assignment
				if (response.activities.includes(this.activity.activity_id)) {
					this.matDialog.open(MessageDialogComponent, {
						data: {
							title: this.translate.instant('modal-rubric-edit_title'),
							message: this.translate.instant('modal-rubric-edit_message')
						}
					});
				// in use on another assignment
				} else {
					this.openCopyRubricModal(true);
				}
			// no permission to edit
			} else if (!response.can_edit) {
				this.openCopyRubricModal(false);
			// not in use anywhere can edit
			} else {
				this.rubricEditor.open({
					modalData: {
						options: {
							mode: RUBRIC_VIEWER_EDIT_MODE,
							previewOnly: false,
							postData: {
								group_id: this.activity.group_id
							},
							schemaId: this.selectedRubric.id
						}
					}
				}).result.then((template: RubricTemplate) => {
					this.setSelectedRubric(template);
				});
			}
		});
	}

	public openCopyRubricModal (isInUse: boolean) {
		this.matDialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('modal-rubric-edit-copy_title'),
				message: isInUse ? this.translate.instant('modal-rubric-edit-decouple_message') : this.translate.instant('modal-rubric-edit-no-permission_message'),
				confirmText: this.translate.instant('common_create-copy'),
				cancelText: this.translate.instant('common_cancel')
			}
		}).afterClosed().subscribe((confirm: boolean) => {
			if (confirm) {
				this.libraryService.copyRubricTemplate(this.selectedRubric.id).subscribe((res) => {
					this.selectedRubric = res;
					this.rubricEditor.open({
						modalData: {
							options: {
								mode: RUBRIC_VIEWER_EDIT_MODE,
								previewOnly: false,
								postData: {
									group_id: this.activity.group_id
								},
								schemaId: this.selectedRubric.id
							}
						}
					}).result.then((template: RubricTemplate) => {
						this.setSelectedRubric(template);
					});
				});
			}
		});
	}

	public handleRemove (): void {
		//if rubric is in use on this activity, don't let it be removed
		this.libraryService.checkRubricUsage(this.selectedRubric.id).subscribe((response) => {
			if (response.in_use &&
				response.activities.includes(this.activity.activity_id)) {
				this.matDialog.open(MessageDialogComponent, {
					data: {
						title: this.translate.instant('modal-rubric-delete_title'),
						message: this.translate.instant('modal-rubric-delete_message')
					}
				});
			//else just remove with no confirmation
			} else {
				this.setSelectedRubric(null);
				const fallback: GradingTypeKey = this.gradingTypeLookup.NOT_GRADED.disabled ? 'POINTS' : 'NOT_GRADED';
				this.gradingTypeControl.setValue(fallback);
				this.cdr.markForCheck();
			}
		});
	}

	private determineInitiallySelectedGradingType (): GradingTypeKey {
		if (this.activity?.rubric_template_id && this.activity.rubric_template_id > 0) {
			return 'INSTRUCTOR_RUBRIC';
		}

		if (this.activity?.total_score != null) {
			return 'POINTS';
		}

		return this.gradingTypeLookup.NOT_GRADED.disabled ? 'POINTS' : 'NOT_GRADED';
	}

	private onGradingTypeSelectionChange (newValue: GradingTypeKey): void {
		const previousValue = this.selectedGradingTypeKey;
		if (newValue === previousValue) {
			return;
		}

		this.selectedGradingTypeKey = newValue;
		this.handleGradingTypeUpdate();
		this.updatePointsValidators();

		switch (newValue) {
			case 'NOT_GRADED':
				this.setSelectedRubric(null);
				this.setPointsPossible(null);
				break;
			case 'POINTS':
				this.setSelectedRubric(null);
				if (this.activity?.total_score !== null && this.activity?.total_score !== undefined) {
					const parsed = Number(this.activity.total_score);
					this.setPointsPossible(isNaN(parsed) ? null : parsed);
				} else {
					this.setPointsPossible(null);
				}
				break;
			case 'INSTRUCTOR_RUBRIC':
				this.openRubricSelection(previousValue);
				break;
		}

		this.cdr.markForCheck();
	}

	private updatePointsValidators (): void {
		const validators = [this.pointsNumberValidator.bind(this)];
		if (this.selectedGradingTypeKey === 'POINTS') {
			validators.unshift(Validators.required);
		}

		this.pointsControl.setValidators(validators);
		this.pointsControl.updateValueAndValidity({ emitEvent: false });
	}

	private pointsNumberValidator (control: AbstractControl): Record<string, boolean> | null {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const parsed = Number(value);
		if (isNaN(parsed)) {
			return { invalidNumber: true };
		}

		if (parsed < 0) {
			return { min: true };
		}

		return null;
	}

	private handleGradingTypeUpdate (): void {
		if (this.reviewersAccessControl.value) {
			this.reviewersAccessControl.setValue(false, { emitEvent: false });
			this.reviewersAccessControl.markAsDirty();
		}
	}

	private setPointsPossible (value: number | null, markDirty: boolean = true): void {
		this.pointsControl.setValue(value, { emitEvent: false });
		if (markDirty) {
			this.pointsControl.markAsDirty();
		}
	}

	private setSelectedRubric (rubricTemplate: RubricTemplate | null, markDirty: boolean = true): void {
		this.selectedRubric = rubricTemplate;

		const rubricId = rubricTemplate?.id ?? null;
		if (this.rubricTemplateControl) {
			this.rubricTemplateControl.setValue(rubricId, { emitEvent: false });
			if (markDirty) {
				this.rubricTemplateControl.markAsDirty();
			}
		}

		let numericTotal: number | null = null;
		const total = rubricTemplate?.total;
		if (total !== null && total !== undefined && total !== '') {
			const parsed = Number(total);
			numericTotal = isNaN(parsed) ? null : parsed;
		}

		this.setPointsPossible(numericTotal, markDirty);
	}

	private openRubricSelection (previousValue: GradingTypeKey): void {
		this.ngxGoModalService.open(LibraryDialogComponent, false, {
			data: {
				filterType: [CONTENT_TYPES.RUBRICS],
				mode: MODES.SELECT,
				collectionManagement: true
			}
		}).afterClosed().subscribe((rubricTemplate: RubricTemplate) => {
			if (rubricTemplate) {
				this.setSelectedRubric(rubricTemplate);
				this.updatePointsValidators();
				this.cdr.markForCheck();
			} else {
				this.selectedGradingTypeKey = previousValue;
				this.gradingTypeControl.setValue(previousValue, { emitEvent: false });
				this.updatePointsValidators();
				this.cdr.markForCheck();
			}
		});
	}

	private loadSelectedRubric (rubricId: number): void {
		this.isLoadingRubric = true;
		this.libraryService.getRubricTemplate(rubricId)
			.pipe(
				take(1),
				takeUntil(this.destroy$),
				finalize(() => {
					this.isLoadingRubric = false;
					this.cdr.markForCheck();
				})
			)
			.subscribe({
				next: (rubricTemplate) => {
					this.setSelectedRubric(rubricTemplate, false);
				},
				error: () => {
					this.setSelectedRubric(null, false);
				}
			});
	}

	private get isPointsReadonly (): boolean {
		return !!this.activity.is_points_possible_readonly;
	}
}

